export default class FileProcessingModule {
  constructor({
    notifications,
    visitDate,
    subjectCode = null,
    studyName = null,
    sdarUrl = null,
  }) {
    this.notifications = notifications;
    this.visitDate = visitDate;
    this.subjectCode = subjectCode;
    this.studyName = studyName;
    this.sdarUrl = sdarUrl;
  }

  handleGlobalFileUpload(gradingFormElem, event) {
    this.notifications.showLoader();
    const { file, eyeSection, modality } = event.detail;
    const dropzone = event.detail.dropzone;
    if (file) {
      this.processReceivedFile(file, eyeSection, modality, gradingFormElem, dropzone);
    } else {
      this.notifications.warning(
        "No file selected. Please select an image file to proceed."
      );
      this.notifications.hideLoader();
    }
  }

  handleGlobalFileDelete(gradingFormElem, event) {
    const { file, eyeSection } = event.detail;

    // Select all elements with the `data-screenshot` attribute
    const inputs = document.querySelectorAll("[data-screenshot]");

    inputs.forEach((component) => {
      // Split the `data-screenshot` attribute to get the key and side
      const [key, side] = component.getAttribute("data-screenshot").split("-");

      // Check if the side matches the eyeSection from the event
      if (side === eyeSection) {
        // Clear the value of the component
        component.value = ""; // Clear the input value
        component.setAttribute("value", ""); // Ensure the value attribute is also cleared

        // Trigger an input event to notify any listeners
        const inputEvent = new Event("input", { bubbles: true });
        component.dispatchEvent(inputEvent);
      }
    });

    this.notifications.success(
      `Data related to file ${file.name} has been removed from the form.`
    );
  }

  processReceivedFile(file, eyeSection, modality, form, dropzone) {
    const reader = new FileReader();
    reader.onload = () => {
      const base64Image = reader.result.split(",")[1];
      this.performOcr(base64Image, eyeSection, modality, form, file, dropzone);
    };
    reader.onerror = (error) => {
      console.error("Error reading file:", error);
      this.notifications.warning(
        "Unable to read the file. Please ensure it is a valid image file and try again."
      );
      this.notifications.hideLoader();
    };
    try {
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Unexpected error during file reading:", error);
      this.notifications.warning(
        "An unexpected error occurred while reading the file. Please ensure the file format is correct and try again."
      );
      this.notifications.hideLoader();
    }
  }

  performOcr(base64Image, eyeSection, modality, form, file, dropzone) {
    const url = `${this.sdarUrl}${modality}/`;
    let inputJSON;
    if (modality === "sm") {
      inputJSON = { image: base64Image };
    } else {
      inputJSON = { pdf: base64Image };
    }

    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(inputJSON),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Server returned an error: HTTP status ${response.status}`
          );
        }
        return response.json();
      })
      .then((data) => {
        console.log(data);
        if (data && data.data && data.data.patient_details) {
          const isDataUpdated = this.updateFormWithData(eyeSection, data)
            .then((isDataUpdated) => {
              if (isDataUpdated) {
                this.notifications.success("File processed successfully!");
              } else {
                this.emitRemoveEvent(file, dropzone);
              } // Now it will log after confirmation
            })
            .catch((error) => {
              console.error("Error in confirmation:", error);
            });
        } else {
          throw new Error(
            "Unexpected response format from the server. Please contact support."
          );
        }
      })
      .catch((error) => {
        console.error("File processing error:", error);
        this.notifications.warning(
          `An error occurred while processing the file. ${
            error.message || "Please try again."
          }`
        );
      })
      .finally(() => {
        this.notifications.hideLoader();
      });
  }

  updateFormWithData(eyeSection, data) {
    return new Promise((resolve, reject) => {
      if (!data || !data.data || !data.data.patient_details) {
        this.notifications
          .warning("Invalid data provided.")
          .then((result) => {
            if (result.isConfirmed) {
              this.notifications.hideLoader();
              resolve(false);
            }
          })
          .catch((error) => {
            console.error("Error handling warning:", error);
            this.notifications.hideLoader();
            reject(error);
          });
        return;
      }

      const { laterality, examDate, studyName, patientId } =
        data.data.patient_details;
      let mismatches = [];
      const formattedExamDate = this.convertDateFormat(examDate);
      if (!laterality) {
        mismatches.push("Eye is missing");
      } else if (eyeSection !== laterality && laterality !== "OU") {
        mismatches.push("Eye is NOT matching");
      }

      if (this.visitDate !== formattedExamDate) {
        mismatches.push("Visit date is NOT matching");
      }

      if (this.studyName !== studyName) {
        mismatches.push("Study ID is NOT matching");
      }

      if (this.subjectCode !== patientId) {
        mismatches.push("Subject ID is NOT matching");
      }

      if (mismatches.length > 0) {
        const message = `<p class='text-base'>The information extracted from the PDF report is NOT matching the one in EyeSol for the following items:\n- ${mismatches.join(
          "\n- "
        )}\n</p>`;

        this.notifications
          .confirmAction(message)
          .then((result) => {
            if (result.isConfirmed) {
              this.notifications.hideLoader();
              this.processData(data, laterality);
              resolve(true);
            } else {
              console.log("User canceled, stopping execution.");
              this.notifications.hideLoader();
              resolve(false);
            }
          })
          .catch((error) => {
            console.error("Error in confirmation:", error);
            this.notifications.hideLoader();
            reject(error);
          });
      } else {
        this.processData(data, laterality);
        resolve(true);
      }
    });
  }
  convertDateFormat(dateStr) {
    if (!dateStr) return null;
  
    const dateFormats = [
      { regex: /^([A-Za-z]{3})\/(\d{1,2})\/(\d{4})$/, format: "MMM/DD/YYYY" },
      { regex: /^(\d{2})\/(\d{2})\/(\d{4})$/, format: "MM/DD/YYYY" },
      { regex: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, format: "M/D/YYYY" },
      { regex: /^(\d{4})-(\d{2})-(\d{2})$/, format: "YYYY-MM-DD" },
    ];
  
    for (let { regex, format } of dateFormats) {
      const match = dateStr.match(regex);
      if (match) {
        let year, month, day;
  
        if (format === "MMM/DD/YYYY") {
          const months = {
            Jan: "01", Feb: "02", Mar: "03", Apr: "04", May: "05", Jun: "06",
            Jul: "07", Aug: "08", Sep: "09", Oct: "10", Nov: "11", Dec: "12",
          };
          [ , month, day, year] = match;
          month = months[month];
        } else if (format === "MM/DD/YYYY" || format === "M/D/YYYY") {
          [ , month, day, year] = match;
          month = month.padStart(2, "0");
          day = day.padStart(2, "0");
        } else if (format === "YYYY-MM-DD") {
          return dateStr;
        }
  
        return `${year}-${month}-${day}`;
      }
    }
  
    console.warn("Unrecognized date format:", dateStr);
    return dateStr;
  }
  processData(data, laterality) {
    const inputs = document.querySelectorAll("[data-screenshot]");
    inputs.forEach((component) => {
      const [key, side] = component.getAttribute("data-screenshot").split("-");
      let value = null; 

      if (laterality === "OU") {
        if (side === "OS" || side === "OD") {
        

              value = 
  data.data?.[side]?.etdrs_volume?.[key] || 
  data.data?.[side]?.etdrs_thickness?.[key] || 
  data.data?.[side]?.total_region_size || 
  "No Data";
        }
      } else if (side === laterality) {
        value = 
  data.data?.[laterality]?.etdrs_volume?.[key] || 
  data.data?.[laterality]?.etdrs_thickness?.[key] || 
  data.data?.[laterality]?.[key] || 
  "No Data";
      }

      if (value !== null && value !== "N/A") {
        component.value = value;
        component.setAttribute("value", value);
        const event = new Event("input", { bubbles: true });
        component.dispatchEvent(event);
      } else if (side === laterality || laterality === "OU") {
        component.setAttribute("value", " ");
        const event = new Event("input", { bubbles: true });
        component.dispatchEvent(event);
      }
    });
  }

  // Method to initialize the Dropzone controller
  initializeDropzoneController() {
    this.dropzoneController =
      this.application.getControllerForElementAndIdentifier(
        this.element,
        "dropzone"
      );

    if (!this.dropzoneController) {
      console.error("DropzoneController not found.");
    }
  }

  emitRemoveEvent(file, dropzone) {
    // Dispatch the custom event to notify other parts of the application
    const event = new CustomEvent("removeFile", {
      detail: {
        fileName: file.name,
      },
      bubbles: true,
    });
    window.dispatchEvent(event);
    // Remove only the specified file from Dropzone
    if (dropzone && file) {
      dropzone.removeFile(file); // This removes the specific file from Dropzone
    }
  }
}
